import { message } from "antd";
import { type UploadFileStatus } from "antd/lib/upload/interface";
import { type BTServiceFileUpload } from "legacyComponents/FileUploadContainer.types";
import { useRef } from "react";

import { BTText } from "@btnet/core-components";

import { BdsIconScan, BdsIconSparkle, BdsIconUploadSimple } from "@buildertrend/components";

import { TempFileTypes } from "types/enum";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { useActionBeingPerformed } from "utilities/form/form";
import yup from "utilities/form/yup";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTForm, BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTModal, type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTDraggerContent } from "commonComponents/btWrappers/BTUpload/BTDraggerContent";
import { BTUpload } from "commonComponents/btWrappers/BTUpload/BTUpload";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import {
    type IBTInjectedFormikProps,
    withFormikBT,
} from "commonComponents/utilities/Formik/WithFormikBT";
import { FormikValidationSummary } from "commonComponents/utilities/validationSummary/FormikValidationSummary/FormikValidationSummary";

import "./BulkBillUploadModal.styles.less";

import {
    BulkBillUploadHandler,
    type IBulkBillUploadHandler,
} from "./BulkBillUploadModal.api.handler";
import {
    ALLOWED_BILL_UPLOAD_FILE_TYPES,
    type BulkBillUploadFormActions,
    type IBulkBillUploadFormValues,
} from "./BulkBillUploadModal.api.types";

const allowedFileTypes = ALLOWED_BILL_UPLOAD_FILE_TYPES;

const BulkBillUploadValidators = yup.object().shape<IBulkBillUploadFormValues>({
    uploadedFiles: yup.array(),
});

export interface IBulkBillUploadModalProps {
    handler?: IBulkBillUploadHandler;
    modalConfig: IModalConfiguration;
    onUploadComplete?: (batchId: string, fileNames: string[]) => void;
}

const defaultHandler = new BulkBillUploadHandler();

const BulkBillUploadModal = ({
    errors,
    setFieldValue,
    values,
    submitCount,
    handler = defaultHandler,
    modalConfig,
    onUploadComplete,
}: IBTInjectedFormikProps<IBulkBillUploadModalProps, IBulkBillUploadFormValues>) => {
    const [actionBeingPerformed, setLoadingAction] =
        useActionBeingPerformed<BulkBillUploadFormActions>();
    const isUploadingRef = useRef(false);

    const performUpload = async () => {
        try {
            if (values.uploadedFiles.length > 0) {
                const response = await handler.bulkOcrUpload(values.uploadedFiles);
                const count = response.queuedCount ?? 0;
                void message.success(
                    `${count} bill${count === 1 ? "" : "s"} queued for processing.`,
                );
                if (onUploadComplete && response.batchId) {
                    const fileNames = values.uploadedFiles.map((f) => f.fileName ?? "");
                    onUploadComplete(response.batchId, fileNames);
                }
                modalConfig.beforeClose();
            } else {
                void message.error("Please upload at least one file.");
            }
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    const handleSubmit = async () => {
        if (isUploadingRef.current) {
            await setLoadingAction({
                actionBeingPerformed: "upload",
                callback: async () => {},
                callbackNavigates: false,
            });
            return;
        }
        await setLoadingAction({
            actionBeingPerformed: "upload",
            callback: performUpload,
        });
    };

    const handleStatusChange = async (statuses: (UploadFileStatus | undefined)[]) => {
        const uploading = statuses.some((s) => s === "uploading");
        if (actionBeingPerformed === "upload" && !uploading) {
            await setLoadingAction({
                actionBeingPerformed: "upload",
                callback: async () => {
                    if (statuses.every((s) => s === "done")) {
                        await performUpload();
                    }
                },
            });
            isUploadingRef.current = false;
        } else {
            isUploadingRef.current = uploading;
        }
    };

    return (
        <BTModal
            data-testid="btModalBulkBillUpload"
            visible
            width="600px"
            removeBodyPadding
            beforeClose={modalConfig.beforeClose}
            useModalLayout
        >
            <BTModalLayout
                title={
                    <BTRow align="middle">
                        <BdsIconSparkle className="margin-right-xs" />
                        <BTText
                            text="Upload bills and receipts"
                            size="distinct-lg"
                            className="margin-top-zero margin-bottom-zero"
                        />
                    </BTRow>
                }
                closable
                footerContent={
                    <>
                        <BTButton
                            data-testid="bulkBillUploadSubmit"
                            type="primary"
                            onClick={handleSubmit}
                            loading={actionBeingPerformed === "upload"}
                            icon={<BdsIconSparkle />}
                            disabled={values.uploadedFiles.length === 0}
                        >
                            Create draft bills
                        </BTButton>
                        <BTButton
                            type="secondary"
                            data-testid="bulkBillUploadCancel"
                            onClick={() => modalConfig.beforeClose()}
                            loading={actionBeingPerformed === "cancel"}
                        >
                            Cancel
                        </BTButton>
                    </>
                }
                modalConfig={modalConfig}
                className="BulkBillUploadModal"
            >
                <BTRow>
                    <BTText
                        text="We'll do the data entry for you."
                        size="normal-md"
                        className="margin-bottom-sm"
                    />
                </BTRow>
                <BTForm>
                    <FormikValidationSummary
                        errors={errors}
                        values={values}
                        showAfterSubmit={submitCount}
                        scheme={BulkBillUploadValidators}
                    />
                    <BTFormItem>
                        <BTUpload
                            id="uploadedFiles"
                            data-testid="bulkBillUploadFiles"
                            setFieldValue={(
                                field: string,
                                value: BTServiceFileUpload | BTServiceFileUpload[] | undefined,
                            ) => setFieldValue(field, value)}
                            value={values.uploadedFiles}
                            allowedFileTypes={allowedFileTypes}
                            multiple
                            tempFileType={TempFileTypes.Receipt}
                            isDragger
                            useBdsButtonStyle
                            uploadConfig={{ shouldUploadFullResolutionPhoto: true }}
                            onStatusChange={handleStatusChange}
                            hideDraggerDefaultContent
                        >
                            <BTRow justify="center">
                                <BdsIconScan size="3xl" className="scanIcon margin-bottom-sm" />
                            </BTRow>
                            <BTRow justify="center">
                                <BTText text="Drag and drop files" size="distinct-md" />
                            </BTRow>
                            <BTRow justify="center">
                                <BTText text="OR" size="normal-md" className="margin-top-xs" />
                            </BTRow>
                            <BTButton
                                data-testid="BulkUploadScanIcon"
                                type="secondary"
                                icon={<BdsIconUploadSimple />}
                                className="margin-top-xs margin-bottom-sm"
                            >
                                Browse
                            </BTButton>
                            <BTDraggerContent
                                allowedFileTypes={allowedFileTypes}
                                isPreferred
                                description=""
                            />
                        </BTUpload>
                    </BTFormItem>
                </BTForm>
            </BTModalLayout>
        </BTModal>
    );
};

const FormWithErrorBoundary = withErrorBoundary(BulkBillUploadModal)(
    "Could not load Bulk Bill Upload",
);

export default withFormikBT<IBulkBillUploadModalProps, IBulkBillUploadFormValues>({
    mapPropsToValues: () => ({
        uploadedFiles: [],
    }),
    validationSchema: BulkBillUploadValidators,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    handleSubmit: () => {
        // TODO: move when upgrading to formik v4
    },
})(FormWithErrorBoundary);
